import { FileMosaic } from "@files-ui/react";
import he from "he";
import parse from "html-react-parser";
import { useEffect, useRef, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import {
  LuChevronDown,
  LuCopy,
  LuLoaderCircle,
  LuTriangleAlert,
} from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import * as SharedMessageTypes from "../../../../../darvin-shared/schema/zod/agent/message-types.schema";
import { useImagePreview } from "../../../../providers/ImagePreviewProvider";
import { useSnackbar } from "../../../../providers/SnackbarProvider";

export function ProjectUserMessage({
  message,
  attachments,
  canBeAnimated,
}: {
  message: string;
  attachments: SharedMessageTypes.UserMessageAttachment[];
  canBeAnimated: boolean;
}) {
  const { openPreview } = useImagePreview();
  const { showSnackbar } = useSnackbar();

  const [mainMessage, setMainMessage] = useState<string | null>(null);
  const [expandableMessage, setExpandableMessage] = useState<string | null>(
    null
  );
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (
      message.includes("@@error_details@@") &&
      message.includes("@@/error_details@@")
    ) {
      setMainMessage(message.split("@@error_details@@")[0]);
      setExpandableMessage(
        he.encode(
          message.split("@@error_details@@")[1].split("@@/error_details@@")[0]
        )
      );
    } else {
      setMainMessage(message);
    }
  }, []);

  return (
    <div
      className={`${
        canBeAnimated && "message-container user-container"
      } w-full flex flex-col items-end gap-2`}
    >
      <div className="flex flex-row flex-wrap gap-2 items-center justify-end">
        {attachments.map((a, index) =>
          a.mimeType.startsWith("image/") ? (
            <LazyImage
              key={index}
              attachment={a}
              onClick={() => openPreview(a)}
            />
          ) : (
            <File
              key={index}
              id={a.filename}
              type={a.mimeType}
              name={a.filename}
            />
          )
        )}
      </div>
      <div
        className="max-w-[80%] py-3 px-5 
        bg-white dark:bg-[#303030]
        tracking-tight rounded-md 
        break-words hyphens-auto message-bubble"
      >
        <div className="whitespace-pre-wrap">{mainMessage}</div>
        {expandableMessage && (
          <div
            className="bg-[#D0D4E4] dark:bg-[#222222]
            border border-[#656A6E40] dark:border-[#373B40]
            rounded-lg mt-4 overflow-hidden"
          >
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`w-full flex items-center justify-between 
                  gap-1 p-4 ${isExpanded && "bg-[#C4C8D9] dark:bg-[#1D1D1D]"}`}
            >
              <div className="flex items-center gap-4">
                <LuTriangleAlert
                  size={20}
                  className="text-[#DBA213] dark:text-[#D8C640]"
                />
                <span className="text-[#303535EB] dark:text-[#76797E]">
                  {isExpanded ? "Hide details" : "Error details"}
                </span>
              </div>
              <LuChevronDown
                size={24}
                className={`transition-transform duration-300 transform ${
                  isExpanded ? "-rotate-180" : "rotate-0"
                }`}
              />
            </button>
            <div
              className={`overflow-y-auto overflow-x-hidden
                  transition-all duration-500 ease-in-out mx-4 ${
                    isExpanded
                      ? "max-h-[1000px] opacity-100 my-4"
                      : "max-h-0 opacity-0 my-0"
                  }`}
            >
              <div className={`flex items-start gap-4 w-full min-w-0`}>
                <span className="break-words hyphens-auto whitespace-pre-wrap flex-1 min-w-0">
                  {parse(expandableMessage)}
                </span>

                <div className="pt-1 flex-shrink-0">
                  <CopyToClipboard
                    text={expandableMessage}
                    onCopy={() => {
                      showSnackbar("Copied to clipboard");
                    }}
                  >
                    <LuCopy
                      size={20}
                      className="hover:cursor-pointer hover:opacity-50 transition-opacity duration-300"
                    />
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LazyImage({
  attachment,
  onClick,
}: {
  attachment: SharedMessageTypes.UserMessageAttachment;
  onClick?: () => void;
}) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  const getSrc = () => {
    if (attachment.content) {
      return `data:image/png;base64,${attachment.content}`;
    } else if (attachment.url) {
      if (import.meta.env.DEV) {
        // Below attachment url is constructed for local development to allow Vite proxy to work
        return attachment.url.replace("https://dev.darvin.dev", "");
      }
      return attachment.url;
    }
    return undefined;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        threshold: 0.5,
      }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onClick={onClick}
      className="relative max-w-[200px] cursor-pointer
    hover:opacity-75 transition-opacity duration-200"
    >
      {!isInView && (
        <div
          className="w-20 h-20 flex items-center justify-center
        rounded-sm border border-light-placeholder dark:border-dark-placeholder"
        >
          <LuLoaderCircle className="animate-spin" size={24} />
        </div>
      )}
      {isInView && (
        <img
          src={getSrc()}
          alt="Attached image"
          loading="lazy"
          style={{
            opacity: isInView ? 1 : 0,
            transition: "opacity 0.3s ease-in",
          }}
          className="max-h-28 w-auto rounded-sm
              border border-light-placeholder dark:border-dark-placeholder"
        />
      )}
    </div>
  );
}

function File({ id, type, name }: { id: string; type: string; name: string }) {
  return (
    <>
      <div
        className="w-[50px] h-[50px] overflow-hidden"
        data-tooltip-id={`file-tooltip-${id}`}
        data-tooltip-content={name}
      >
        <FileMosaic
          id={id}
          type={type}
          name={name}
          darkMode
          style={{ width: "50px", height: "50px", fontSize: "12px" }}
        />
      </div>
      <Tooltip id={`file-tooltip-${id}`} />
    </>
  );
}
